// Lightweight client-side handoff between the Documents Hub Smart AI panel
// and downstream tools (translator, verification, PDF toolkit, checklist).
// Not a security surface — a real signed-URL flow lives in
// src/lib/security/documents.functions.ts. This just carries the *intent*
// (which stored file the user picked and what action they want) so the
// target tool can pre-select fields, target language, checklist items, etc.

export type HandoffAction = "translate" | "verify" | "renew" | "compress" | "scan" | "sign";

export type DocumentHandoff = {
  action: HandoffAction;
  /** Display name of the source document (e.g. "Passport.pdf"). */
  docName: string;
  /** Logical doc type used by the verification tool (passport, visa, ...). */
  docType?: string;
  /** Optional target language for translate flows. */
  targetLanguage?: string;
  /** Optional pre-extracted text to prefill textareas. */
  text?: string;
  /** Optional pointer back into the Wallet (checklist id, storage path, ...). */
  ref?: string;
  /** When the handoff was created — used to expire stale intents. */
  createdAt: number;
};

const KEY = "goh:doc-handoff";
const TTL_MS = 10 * 60 * 1000; // 10 minutes

export function setDocumentHandoff(h: Omit<DocumentHandoff, "createdAt">) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...h, createdAt: Date.now() } satisfies DocumentHandoff),
    );
  } catch {
    // ignore quota / disabled storage
  }
}

/**
 * Read the handoff intended for `action`. Consumed once — subsequent reads
 * return null so a refresh doesn't keep re-prefilling.
 */
export function consumeDocumentHandoff(action?: HandoffAction): DocumentHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DocumentHandoff;
    if (!parsed?.action || !parsed.createdAt) return null;
    if (Date.now() - parsed.createdAt > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    if (action && parsed.action !== action) return null;
    sessionStorage.removeItem(KEY);
    return parsed;
  } catch {
    return null;
  }
}
